import { useMemo, useRef, useState, useEffect, Suspense, Component } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";

/**
 * Detect real WebGL support (WebGL2 or WebGL1) by creating a throwaway
 * context and inspecting for either failure or a software renderer.
 */
function detectWebGL() {
  if (typeof window === "undefined") return false;
  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl2") ||
      canvas.getContext("webgl") ||
      canvas.getContext("experimental-webgl");
    if (!gl) return false;
    // Some sandboxes give a context but no rendering; probe it.
    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    const renderer = ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : "";
    // Software rasterizers still work but often crash under load; allow them.
    if (typeof gl.getError === "function") gl.getError();
    return true;
  } catch (_err) {
    return false;
  }
}

/**
 * Error boundary that catches Three.js/react-three-fiber runtime crashes
 * (context lost, shader compile, etc.) and swaps to the CSS fallback.
 */
class ThreeErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error) {
    // Log for observability but never surface the crash.
    // eslint-disable-next-line no-console
    console.warn("[Scene3D] Falling back to CSS scene:", error?.message || error);
  }
  render() {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}

function MorphBlob() {
  const meshRef = useRef();
  const geoRef = useRef();
  const innerRef = useRef();
  const ringRef = useRef();
  const ring2Ref = useRef();
  const hazeRef = useRef();
  const coreMatRef = useRef();
  const bodyMatRef = useRef();

  const base = useMemo(() => {
    const g = new THREE.IcosahedronGeometry(1.7, 32);
    return g.attributes.position.array.slice(0);
  }, []);

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    const geo = geoRef.current;
    if (!geo) return;
    const pos = geo.attributes.position.array;
    // Sexy heartbeat — faster, more pronounced pulses like a body catching its breath
    const heartbeat =
      0.10 * Math.sin(t * 1.8) +
      0.045 * Math.sin(t * 3.4) +
      0.02 * Math.sin(t * 5.2);
    for (let i = 0; i < pos.length; i += 3) {
      const bx = base[i], by = base[i + 1], bz = base[i + 2];
      const len = Math.sqrt(bx * bx + by * by + bz * bz) || 1;
      const nx = bx / len, ny = by / len, nz = bz / len;
      // Waist pinch — squeezes middle & flares top/bottom (curvier silhouette)
      const waist = 1 - 0.18 * Math.exp(-((ny * 2.4) ** 2));
      // Multi-frequency curvature — silk in a warm breeze with a sexier sway
      const r =
        (1.7 + heartbeat) * waist +
        0.36 * Math.sin(1.7 * bx + t * 0.75) +
        0.28 * Math.sin(2.1 * by + t * 0.95 + 1.1) +
        0.22 * Math.sin(2.6 * bz + t * 0.60);
      pos[i] = nx * r;
      pos[i + 1] = ny * r;
      pos[i + 2] = nz * r;
    }
    geo.attributes.position.needsUpdate = true;
    geo.computeVertexNormals();

    // Pulse emissive intensities to the heartbeat — makes it feel *alive*
    const pulse = 0.5 + 0.5 * Math.sin(t * 1.8);
    if (coreMatRef.current) {
      coreMatRef.current.emissiveIntensity = 1.4 + pulse * 1.2;
    }
    if (bodyMatRef.current) {
      bodyMatRef.current.emissiveIntensity = 0.9 + pulse * 0.6;
    }

    if (meshRef.current) {
      // slow, hypnotic rotation with a sensual side-tilt
      meshRef.current.rotation.y = t * 0.11;
      meshRef.current.rotation.x = Math.sin(t * 0.22) * 0.16;
      meshRef.current.rotation.z = Math.sin(t * 0.18) * 0.05;
    }
    if (innerRef.current) {
      innerRef.current.rotation.y = -t * 0.20;
      innerRef.current.rotation.z = t * 0.09;
      // core "breathes" — grows on the pulse
      const s = 0.66 + pulse * 0.06;
      innerRef.current.scale.set(s, s, s);
    }
    if (ringRef.current) {
      ringRef.current.rotation.z = t * 0.35;
      ringRef.current.rotation.x = Math.PI / 2 + Math.sin(t * 0.4) * 0.25;
    }
    if (ring2Ref.current) {
      ring2Ref.current.rotation.z = -t * 0.28;
      ring2Ref.current.rotation.y = Math.sin(t * 0.5) * 0.4;
    }
    if (hazeRef.current) {
      const hs = 1.55 + pulse * 0.05;
      hazeRef.current.scale.set(hs, hs, hs);
    }
  });

  return (
    <group>
      {/* Inner glowing core — HOT magenta, throbbing */}
      <mesh ref={innerRef} scale={0.68}>
        <sphereGeometry args={[1.0, 48, 48]} />
        <meshStandardMaterial
          ref={coreMatRef}
          color="#FF2E63"
          emissive="#FF1454"
          emissiveIntensity={1.6}
          roughness={0.18}
          metalness={0.4}
          transparent
          opacity={0.95}
        />
      </mesh>

      {/* Main silk-morph body — molten rose-gold */}
      <mesh ref={meshRef} scale={1.30}>
        <icosahedronGeometry ref={geoRef} args={[1.7, 32]} />
        <meshPhysicalMaterial
          ref={bodyMatRef}
          color="#E63267"
          emissive="#6E0A28"
          emissiveIntensity={1.0}
          metalness={0.7}
          roughness={0.18}
          clearcoat={0.85}
          clearcoatRoughness={0.25}
          sheen={1}
          sheenColor="#FFC773"
          transparent
          opacity={0.94}
        />
      </mesh>

      {/* Gold aura ring — orbits like a halo of jewellery */}
      <mesh ref={ringRef} scale={2.2}>
        <torusGeometry args={[1, 0.012, 16, 128]} />
        <meshBasicMaterial color="#F0C377" transparent opacity={0.7} blending={THREE.AdditiveBlending} />
      </mesh>

      {/* Second thinner rose ring, counter-rotating */}
      <mesh ref={ring2Ref} scale={2.55}>
        <torusGeometry args={[1, 0.006, 12, 128]} />
        <meshBasicMaterial color="#FF6EA1" transparent opacity={0.55} blending={THREE.AdditiveBlending} />
      </mesh>

      {/* Outer haze shell — subtle rose halo that breathes with the pulse */}
      <mesh ref={hazeRef} scale={1.55}>
        <sphereGeometry args={[1.7, 32, 32]} />
        <meshBasicMaterial color="#FF3D75" transparent opacity={0.10} />
      </mesh>
    </group>
  );
}

function Particles() {
  const ref = useRef();
  const rose = useRef();
  // Denser + spiraling — champagne bubbles rising through velvet
  const count = 620;
  const roseCount = 260;

  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const radius = 2.4 + Math.random() * 6.5;
      arr[i * 3]     = Math.cos(angle) * radius;
      arr[i * 3 + 1] = (Math.random() - 0.5) * 8;
      arr[i * 3 + 2] = Math.sin(angle) * radius;
    }
    return arr;
  }, []);
  const seeds = useMemo(
    () => new Float32Array(Array.from({ length: count }, () => Math.random())),
    []
  );

  // Hot pink counter-swirl — tighter to the blob
  const rosePositions = useMemo(() => {
    const arr = new Float32Array(roseCount * 3);
    for (let i = 0; i < roseCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const radius = 1.9 + Math.random() * 3.5;
      arr[i * 3]     = Math.cos(angle) * radius;
      arr[i * 3 + 1] = (Math.random() - 0.5) * 5;
      arr[i * 3 + 2] = Math.sin(angle) * radius;
    }
    return arr;
  }, []);
  const roseSeeds = useMemo(
    () => new Float32Array(Array.from({ length: roseCount }, () => Math.random())),
    []
  );

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    if (ref.current) {
      const pos = ref.current.geometry.attributes.position.array;
      for (let i = 0; i < count; i++) {
        const seed = seeds[i];
        // Upward drift, wrap
        pos[i * 3 + 1] += 0.010 + seed * 0.008;
        if (pos[i * 3 + 1] > 4) pos[i * 3 + 1] = -4;
        // Orbital wobble
        const wobble = 0.0025 * Math.sin(t * 0.75 + seed * 6.28);
        const x = pos[i * 3], z = pos[i * 3 + 2];
        pos[i * 3]     = x * Math.cos(wobble) - z * Math.sin(wobble);
        pos[i * 3 + 2] = x * Math.sin(wobble) + z * Math.cos(wobble);
      }
      ref.current.geometry.attributes.position.needsUpdate = true;
      ref.current.rotation.y = t * 0.03;
      // Twinkle — pulse global material opacity
      ref.current.material.opacity = 0.75 + 0.2 * Math.sin(t * 1.4);
    }
    if (rose.current) {
      const pos = rose.current.geometry.attributes.position.array;
      for (let i = 0; i < roseCount; i++) {
        const seed = roseSeeds[i];
        // Downward counter-drift for depth
        pos[i * 3 + 1] -= 0.008 + seed * 0.006;
        if (pos[i * 3 + 1] < -3) pos[i * 3 + 1] = 3;
        const wobble = -0.0035 * Math.sin(t * 0.9 + seed * 6.28);
        const x = pos[i * 3], z = pos[i * 3 + 2];
        pos[i * 3]     = x * Math.cos(wobble) - z * Math.sin(wobble);
        pos[i * 3 + 2] = x * Math.sin(wobble) + z * Math.cos(wobble);
      }
      rose.current.geometry.attributes.position.needsUpdate = true;
      rose.current.rotation.y = -t * 0.05;
      rose.current.material.opacity = 0.55 + 0.35 * Math.sin(t * 2.1 + 1.3);
    }
  });
  return (
    <group>
      {/* Gold rose specks — larger, slower */}
      <points ref={ref}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" args={[positions, 3]} />
        </bufferGeometry>
        <pointsMaterial
          size={0.065}
          color="#F0C377"
          transparent
          opacity={0.85}
          sizeAttenuation
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </points>
      {/* Hot magenta twinkles — tighter orbit */}
      <points ref={rose}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" args={[rosePositions, 3]} />
        </bufferGeometry>
        <pointsMaterial
          size={0.045}
          color="#FF3D75"
          transparent
          opacity={0.75}
          sizeAttenuation
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </points>
    </group>
  );
}

/**
 * CSS-only fallback hero — animated gradient blobs + floating gold specks.
 * Same colour language as the WebGL scene so the site stays on-brand.
 */
function CSSFallbackScene() {
  const specks = useMemo(
    () =>
      Array.from({ length: 40 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        top: Math.random() * 100,
        size: Math.random() * 3 + 1,
        delay: Math.random() * 8,
        duration: 6 + Math.random() * 6,
      })),
    []
  );
  return (
    <div
      data-testid="hero-3d-fallback"
      className="absolute inset-0 overflow-hidden"
      aria-hidden="true"
    >
      {/* Blob 1 — gold */}
      <div
        className="absolute h-[60vmin] w-[60vmin] rounded-full opacity-70 blur-3xl"
        style={{
          background: "radial-gradient(circle at 30% 30%, #D2A857 0%, transparent 65%)",
          top: "18%",
          left: "22%",
          animation: "vixBlobA 22s ease-in-out infinite",
        }}
      />
      {/* Blob 2 — rose */}
      <div
        className="absolute h-[55vmin] w-[55vmin] rounded-full opacity-60 blur-3xl"
        style={{
          background: "radial-gradient(circle at 60% 40%, #C15F76 0%, transparent 65%)",
          top: "34%",
          left: "48%",
          animation: "vixBlobB 26s ease-in-out infinite",
        }}
      />
      {/* Blob 3 — ember */}
      <div
        className="absolute h-[45vmin] w-[45vmin] rounded-full opacity-50 blur-2xl"
        style={{
          background: "radial-gradient(circle at 50% 50%, #5a1622 0%, transparent 70%)",
          top: "25%",
          left: "38%",
          animation: "vixBlobC 30s ease-in-out infinite",
        }}
      />
      {/* Gold specks */}
      {specks.map((s) => (
        <span
          key={s.id}
          className="absolute rounded-full bg-velvet-gold/70"
          style={{
            left: `${s.left}%`,
            top: `${s.top}%`,
            width: `${s.size}px`,
            height: `${s.size}px`,
            animation: `vixSpeck ${s.duration}s ease-in-out ${s.delay}s infinite`,
            boxShadow: "0 0 8px rgba(210,168,87,0.6)",
          }}
        />
      ))}
      <style>{`
        @keyframes vixBlobA {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33%      { transform: translate(6vw, -4vh) scale(1.08); }
          66%      { transform: translate(-3vw, 5vh) scale(0.94); }
        }
        @keyframes vixBlobB {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50%      { transform: translate(-8vw, 3vh) scale(1.12); }
        }
        @keyframes vixBlobC {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.5; }
          50%      { transform: translate(4vw, -6vh) scale(0.9); opacity: 0.65; }
        }
        @keyframes vixSpeck {
          0%, 100% { opacity: 0.15; transform: translateY(0) scale(1); }
          50%      { opacity: 0.9;  transform: translateY(-14px) scale(1.25); }
        }
      `}</style>
    </div>
  );
}

export default function Scene3D() {
  const [webglOk, setWebglOk] = useState(null); // null = checking

  useEffect(() => {
    setWebglOk(detectWebGL());
  }, []);

  // Loading state (very short) — render fallback ambience so hero isn't blank.
  if (webglOk === null) return <CSSFallbackScene />;
  if (webglOk === false) return <CSSFallbackScene />;

  return (
    <ThreeErrorBoundary fallback={<CSSFallbackScene />}>
      <Canvas
        camera={{ position: [0, 0, 5.2], fov: 45 }}
        dpr={[1, 2]}
        gl={{ antialias: true, alpha: true, failIfMajorPerformanceCaveat: false }}
        data-testid="hero-3d-canvas"
        className="absolute inset-0"
        onCreated={({ gl }) => {
          // Handle browser-issued context loss without crashing.
          const el = gl.domElement;
          const onLost = (e) => {
            e.preventDefault();
            // eslint-disable-next-line no-console
            console.warn("[Scene3D] WebGL context lost — using CSS fallback");
            setWebglOk(false);
          };
          el.addEventListener("webglcontextlost", onLost, false);
        }}
        onError={(err) => {
          // eslint-disable-next-line no-console
          console.warn("[Scene3D] Canvas error:", err?.message || err);
          setWebglOk(false);
        }}
      >
        <Suspense fallback={null}>
          <ambientLight intensity={0.5} />
          <pointLight position={[4, 3, 4]} intensity={110} color="#FFD98A" />
          <pointLight position={[-5, -2, 3]} intensity={95} color="#FF2E63" />
          <pointLight position={[0, 4, -4]} intensity={55} color="#ffffff" />
          <pointLight position={[2, -4, 2]} intensity={55} color="#F0C377" />
          <pointLight position={[-3, 2, -3]} intensity={45} color="#FF6EA1" />
          <MorphBlob />
          <Particles />
        </Suspense>
      </Canvas>
    </ThreeErrorBoundary>
  );
}
